import React, { useEffect, useMemo, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { type Assignment, type Content } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'

import { AssessmentModalForm } from './AssessmentModalForm'
import {
  ACTIVE_STATUS,
  type AssessmentFrequency,
  FLEXIBLE_ASSESSMENT_STAGE,
  getAddAssessmentBanner,
  getFrequencyOptions,
  getLatestFlexibleAssignment,
  PAUSED_STATUS,
} from './assessmentModalHelpers'
import {
  type FlexibleAssessment,
  ONE_OFF_FREQUENCY,
  useGetFlexibleAssessmentsQuery,
  useSetFlexibleAssessmentMutation,
  useUpdateFlexibleAssessmentMutation,
} from './data/flexibleAssessmentsApi'
import { getLyraTherapyAssessmentContentsData } from '../../data/lyraTherapy/contentSelectors'
import { useAppDispatch } from '../../data/store'
import { setToastContent } from '../data/ltToastAutoActions'

export type AddAssessmentModalProps = {
  visible: boolean
  episodeId: string
  clientName: string
  nextSessionDate?: Date
  assignments?: readonly Assignment[]
  isAssignmentsLoading: boolean
  hasAssignmentsError: boolean
  onRequestClose: () => void
  /** Called when the provider follows the banner link for an assessment they already have. */
  onEditExisting?: (existingAssessment: FlexibleAssessment, assessmentName: string) => void
}

const MESSAGES = defineMessages({
  frequencyDisabledAccessibilityLabel: {
    defaultMessage: 'Frequency. Select an assessment first',
    description: 'Accessibility label for the disabled frequency selector',
  },
  modalTitle: {
    defaultMessage: 'Add assessment',
    description: 'Title and accessibility label for the add assessment modal',
  },
  assessmentScheduled: {
    defaultMessage: '{assessmentName} scheduled for {clientName}',
    description: 'Success toast after scheduling a recurring assessment for a client',
  },
  assessmentEmailSent: {
    defaultMessage: '{assessmentName} sent to {clientName}',
    description: 'Success toast after sending a one-time assessment email to a client',
  },
  assessmentFailed: {
    defaultMessage: "Your assessment couldn't be added. Please try again.",
    description: 'Error shown in the add assessment modal when adding a flexible assessment for a client fails',
  },
  sendEmail: {
    defaultMessage: 'Send email',
    description: 'Button to send a one-time assessment email',
  },
  confirm: {
    defaultMessage: 'Confirm',
    description: 'Button to confirm a recurring assessment',
  },
})

export const AddAssessmentModal: React.FC<AddAssessmentModalProps> = ({
  visible,
  episodeId,
  clientName,
  nextSessionDate,
  assignments,
  isAssignmentsLoading,
  hasAssignmentsError,
  onRequestClose,
  onEditExisting,
}) => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const assessmentContents = useSelector(getLyraTherapyAssessmentContentsData)
  const {
    currentData: flexibleAssessments,
    isError: hasFlexibleAssessmentsError,
    isFetching: isFetchingFlexibleAssessments,
  } = useGetFlexibleAssessmentsQuery({ episodeId })
  const [setFlexibleAssessment, { isLoading: isCreating }] = useSetFlexibleAssessmentMutation()
  const [updateFlexibleAssessment, { isLoading: isUpdating }] = useUpdateFlexibleAssessmentMutation()
  const submissionInFlight = useRef(false)
  const [contentMetaDataId, setContentMetaDataId] = useState('')
  const [frequency, setFrequency] = useState<AssessmentFrequency | ''>('')
  const [submitError, setSubmitError] = useState<string>()

  useEffect(() => {
    if (visible) {
      setContentMetaDataId('')
      setFrequency('')
      setSubmitError(undefined)
      submissionInFlight.current = false
    }
  }, [visible])

  const assessmentOptions = useMemo(
    () =>
      (assessmentContents ?? []).flatMap(({ id, stage, title }: Content) =>
        stage === FLEXIBLE_ASSESSMENT_STAGE && id ? [{ label: title, value: id }] : [],
      ),
    [assessmentContents],
  )
  const selectedAssessment = assessmentOptions.find(({ value }) => value === contentMetaDataId)
  const selectedExistingAssessment = flexibleAssessments?.find(
    ({ content_meta_data_id }) => content_meta_data_id === contentMetaDataId,
  )
  const frequencyOptions = useMemo(() => getFrequencyOptions(formatMessage), [formatMessage])
  const isSendOnce = frequency === ONE_OFF_FREQUENCY
  const isDataReady =
    flexibleAssessments !== undefined &&
    !isFetchingFlexibleAssessments &&
    !hasFlexibleAssessmentsError &&
    assignments !== undefined &&
    !isAssignmentsLoading &&
    !hasAssignmentsError
  const isLoading = isCreating || isUpdating

  const latestFlexibleAssignment = useMemo(
    () =>
      selectedAssessment && assignments
        ? getLatestFlexibleAssignment(assignments, episodeId, selectedAssessment.value)
        : undefined,
    [assignments, episodeId, selectedAssessment],
  )

  // Held back until both queries settle so a stale "no existing assessment" banner never shows.
  const banner = isDataReady
    ? getAddAssessmentBanner({
        formatMessage,
        assessmentName: selectedAssessment?.label,
        frequency,
        existingAssessment: selectedExistingAssessment,
        latestAssignment: latestFlexibleAssignment,
        nextSessionDate,
      })
    : undefined
  const canSubmit = Boolean(selectedAssessment) && frequency !== '' && isDataReady && banner?.type !== 'warning'

  const handleAssessmentChange = (value: string) => {
    setContentMetaDataId(value)
    setFrequency('')
    setSubmitError(undefined)
  }

  const handleFrequencyChange = (value: AssessmentFrequency | '') => {
    setSubmitError(undefined)
    if (value === '') {
      setFrequency('')
      return
    }

    const selectedFrequency = frequencyOptions.find(({ value: optionValue }) => optionValue === Number(value))?.value
    setFrequency(selectedFrequency ?? '')
  }

  const handleRequestClose = () => {
    if (submissionInFlight.current || isLoading) return

    onRequestClose()
  }

  const handleConfirm = async () => {
    if (!canSubmit || !selectedAssessment || isLoading || submissionInFlight.current) return

    submissionInFlight.current = true
    setSubmitError(undefined)

    try {
      if (selectedExistingAssessment) {
        await updateFlexibleAssessment({
          id: selectedExistingAssessment.id,
          frequency,
          previousFrequency: selectedExistingAssessment.frequency,
          previousStatus: selectedExistingAssessment.status,
          ...(selectedExistingAssessment.status === PAUSED_STATUS && { status: ACTIVE_STATUS }),
        }).unwrap()
      } else {
        await setFlexibleAssessment({
          episode_id: episodeId,
          content_meta_data_id: selectedAssessment.value,
          frequency,
        }).unwrap()
      }
    } catch {
      setSubmitError(formatMessage(MESSAGES.assessmentFailed))
      return
    } finally {
      submissionInFlight.current = false
    }

    dispatch(
      setToastContent({
        text: formatMessage(isSendOnce ? MESSAGES.assessmentEmailSent : MESSAGES.assessmentScheduled, {
          assessmentName: selectedAssessment.label,
          clientName,
        }),
        id: 'AssessmentModal-add-success',
        toastType: 'success',
      }),
    )
    onRequestClose()
  }

  return (
    <AssessmentModalForm
      visible={visible}
      mode='add'
      title={formatMessage(MESSAGES.modalTitle)}
      assessmentValue={contentMetaDataId}
      assessmentOptions={assessmentOptions}
      onAssessmentChange={handleAssessmentChange}
      frequency={frequency}
      frequencyOptions={frequencyOptions}
      frequencyDisabled={!selectedAssessment}
      frequencyAccessibilityLabel={
        selectedAssessment ? undefined : formatMessage(MESSAGES.frequencyDisabledAccessibilityLabel)
      }
      onFrequencyChange={handleFrequencyChange}
      banner={banner}
      submitError={submitError}
      confirmText={formatMessage(isSendOnce ? MESSAGES.sendEmail : MESSAGES.confirm)}
      canSubmit={canSubmit}
      isLoading={isLoading}
      onConfirm={() => void handleConfirm()}
      onRequestClose={handleRequestClose}
      onEditExisting={
        selectedExistingAssessment && selectedAssessment
          ? () => onEditExisting?.(selectedExistingAssessment, selectedAssessment.label)
          : undefined
      }
    />
  )
}
